import { CssBaseline, ThemeProvider } from "@mui/material";
import { json } from "@remix-run/node";
import type {
  LinksFunction,
  LoaderFunction,
  MetaFunction,
} from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  useLoaderData,
  useLocation,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { serializeCookieHeader } from "@supabase/ssr";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "./components/AuthProvider";
import Layout from "./components/Layout";
import { ToastProvider } from "./components/ToastProvider";
import { TRPCProvider } from "./components/TRPCProvider";
import { TeamProjectProvider } from "./components/TeamProjectProvider";
import { createAppTheme } from "./theme";
import type { AuthData } from "./ssr/auth.server";
import {
  detectLocaleFromRequest,
  LANG_COOKIE_NAME,
  type LanguageCode,
} from "./utils/languages";
import "./styles/fonts.css";

const SITE_URL = "https://air.cros.team";
const SITE_TITLE = "CROS Air — Communication is management.";
const SITE_DESCRIPTION =
  "Bridge Discord conversations and structured task management. Gemini AI extracts tasks, generates meeting notes, and tracks progress.";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export const meta: MetaFunction = () => [
  { title: SITE_TITLE },
  { name: "description", content: SITE_DESCRIPTION },
  { property: "og:type", content: "website" },
  { property: "og:site_name", content: "CROS Air" },
  { property: "og:title", content: SITE_TITLE },
  { property: "og:description", content: SITE_DESCRIPTION },
  { property: "og:url", content: SITE_URL },
  { property: "og:image", content: OG_IMAGE },
  { property: "og:image:type", content: "image/jpeg" },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { property: "og:image:alt", content: SITE_TITLE },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: SITE_TITLE },
  { name: "twitter:description", content: SITE_DESCRIPTION },
  { name: "twitter:image", content: OG_IMAGE },
  { name: "theme-color", content: "#1868DB" },
];

export const links: LinksFunction = () => [
  { rel: "shortcut icon", href: "/favicon.ico" },
  {
    rel: "icon",
    type: "image/png",
    sizes: "32x32",
    href: "/favicon-32x32.png",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "16x16",
    href: "/favicon-16x16.png",
  },
  { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
  { rel: "manifest", href: "/site.webmanifest" },
];

export const loader: LoaderFunction = async ({ request }) => {
  const responseHeaders = new Headers();
  const { getAuthData } = await import("~/ssr/auth.server");
  const authData = await getAuthData(request, responseHeaders);
  const locale = detectLocaleFromRequest(request);
  responseHeaders.append(
    "Set-Cookie",
    serializeCookieHeader(LANG_COOKIE_NAME, locale, {
      path: "/",
      maxAge: 60 * 60 * 24 * 365,
      sameSite: "lax",
    }),
  );
  return json(
    {
      authData,
      locale,
      ENV: {
        BACKEND_URL: process.env.VITE_BACKEND_URL ?? "http://localhost:2023",
      },
    },
    { headers: responseHeaders },
  );
};

const ErrorBoundaryContent = () => {
  const { t } = useTranslation();
  return <div>{t("common.errorOccurred")}</div>;
};

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <ErrorBoundaryContent />;
};

function App() {
  const { authData, locale, ENV } = useLoaderData() as {
    authData: AuthData | null;
    locale: LanguageCode;
    ENV: Record<string, string>;
  };
  const [darkMode] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    const sync = (lng: string) => {
      document.documentElement.lang = lng;
    };
    sync(i18n.language);
    i18n.on("languageChanged", sync);
    return () => {
      i18n.off("languageChanged", sync);
    };
  }, [i18n]);

  const location = useLocation();

  const isAuthRoute = location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/signup") ||
    location.pathname.startsWith("/select-team") ||
    location.pathname.startsWith("/create-team") ||
    location.pathname.startsWith("/connect-messenger") ||
    location.pathname.startsWith("/archived-teams") ||
    location.pathname.startsWith("/api/auth");

  const theme = createAppTheme(darkMode ? "dark" : "light");

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <TRPCProvider serverToken={authData?.accessToken}>
            <ToastProvider>
              <AuthProvider initialAuthData={authData}>
                {isAuthRoute ? (
                  <Outlet />
                ) : (
                  <TeamProjectProvider>
                    <Layout>
                      <Outlet />
                    </Layout>
                  </TeamProjectProvider>
                )}
              </AuthProvider>
            </ToastProvider>
          </TRPCProvider>
        </ThemeProvider>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.__ENV__ = ${JSON.stringify(ENV)}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

export default withSentry(App);